/**
 * The small state readers the map models share (M3 plan §2.6.2): fog knowledge, a relay's live status and the
 * map-unit frame. They read `ReachState` directly so the maps do not wait on R5's state module.
 */
import type { Galaxy, Relay } from '../../galaxy/types'
import type { Knowledge, ReachState, RelayStatus } from '../../reach/types'

/** Danger of a system by index (R14 passes R4's `dangerOf`; the maps never compute it themselves). */
export type DangerFn = (systemIndex: number) => number

/** 'unknown' when the state has no entry (plan §2.6.2: absent = unknown). */
export function knowledgeOf(state: ReachState, systemId: string): Knowledge {
  return state.knowledge[systemId] ?? 'unknown'
}

/** The state's relay status, or the generator's online flag when the state has no entry. */
export function relayStatusOf(state: ReachState, relay: Relay): RelayStatus {
  return state.relays[relay.id]?.status ?? (relay.online ? 'online' : 'offline')
}

/**
 * Galaxy-map units → three.js map space, the same rotation as the system frame (D-TBD 4):
 * `[gx, gy, gz] → [gx, gz, −gy]`, so the disc lies in the x–z plane. Writes into `out` and returns it.
 */
export function mapToThree(pos: readonly [number, number, number], out: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  out.x = pos[0]
  out.y = pos[2]
  out.z = -pos[1]
  return out
}

/** System id → index, built once per galaxy object. */
const indexCache = new WeakMap<Galaxy, Map<string, number>>()

export function systemIndexOf(galaxy: Galaxy, systemId: string): number {
  let index = indexCache.get(galaxy)
  if (index === undefined) {
    index = new Map(galaxy.systems.map((s, i) => [s.id, i]))
    indexCache.set(galaxy, index)
  }
  return index.get(systemId) ?? -1
}
